import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import api from "../api";

const Parametrizacao = () => {
  const [indicadores, setIndicadores] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [modalType, setModalType] = useState("");
  const [descricao, setDescricao] = useState("");
  const [unidadeId, setUnidadeId] = useState("");
  const [nomeUnidade, setNomeUnidade] = useState("");
  const [tipoValor, setTipoValor] = useState("inteiro");

  // Fetch inicial dos dados
  useEffect(() => {
    const fetchData = async () => {
      try {
        const indicadoresRes = await api.get("/indicadores");
        const unidadesRes = await api.get("/unidades");
        setIndicadores(indicadoresRes.data);
        setUnidades(unidadesRes.data);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };
    fetchData();
  }, []);

  // Abrir modal
  const handleOpenModal = (type, data = null) => {
    setModalType(type);
    setModalData(data);

    if (type === "Indicador") {
      setDescricao(data ? data.descricao : "");
      setUnidadeId(data && data.unidade ? data.unidade.id : "");
    } else if (type === "Unidade") {
      setNomeUnidade(data ? data.nome : "");
      setTipoValor(data ? data.tipoValor : "inteiro");
    }
  };

  // Fechar modal
  const handleCloseModal = () => {
    setModalType("");
    setModalData(null);
    setDescricao("");
    setUnidadeId("");
    setNomeUnidade("");
    setTipoValor("inteiro");
  };

  // Salvar alterações
  const handleSave = async () => {
    try {
      if (modalType === "Indicador") {
        const payload = {
          descricao,
          unidade: { id: unidadeId },
          ativo: modalData ? modalData.ativo : true, // Manter o estado atual de ativo
        };
        if (modalData) {
          // Editar Indicador
          await api.put(`/indicadores/${modalData.id}`, payload);
        } else {
          // Criar Novo Indicador
          await api.post("/indicadores", payload);
        }
      } else if (modalType === "Unidade") {
        const payload = {
          nome: nomeUnidade,
          tipoValor,
          ativo: modalData ? modalData.ativo : true, // Manter o estado atual de ativo
        };
        if (modalData) {
          // Editar Unidade
          await api.put(`/unidades/${modalData.id}`, payload);
        } else {
          // Criar Nova Unidade
          await api.post("/unidades", payload);
        }
      }

      // Atualizar as tabelas após salvar
      handleCloseModal();
      const indicadoresRes = await api.get("/indicadores");
      const unidadesRes = await api.get("/unidades");
      setIndicadores(indicadoresRes.data);
      setUnidades(unidadesRes.data);
    } catch (error) {
      console.error("Erro ao salvar dados:", error);
    }
  };

  // Alternar status ativo/inativo
  const handleToggleStatus = async (type, id) => {
    try {
      const endpoint = type === "Indicador" ? "indicadores" : "unidades";
      await api.put(`/${endpoint}/${id}/status`);
      const indicadoresRes = await api.get("/indicadores");
      const unidadesRes = await api.get("/unidades");
      setIndicadores(indicadoresRes.data);
      setUnidades(unidadesRes.data);
    } catch (error) {
      console.error(`Erro ao alternar status de ${type}:`, error);
    }
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography
        variant="h4"
        mb={3}
        sx={{ textAlign: "center", fontWeight: "bold" }}
      >
        Parametrização
      </Typography>

      {/* Indicadores */}
      <Box mb={4}>
        <Typography
          variant="h5"
          sx={{ color: "primary.main", fontWeight: "bold" }}
        >
          Indicadores
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenModal("Indicador")}
          sx={{ marginTop: 2 }}
        >
          Novo Indicador
        </Button>
        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#003366" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Descrição
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Unidade
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {indicadores.map((indicador) => (
                <TableRow key={indicador.id}>
                  <TableCell>{indicador.descricao}</TableCell>
                  <TableCell>{indicador.unidade?.nome || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={indicador.ativo ? "Ativo" : "Inativo"}
                      color={indicador.ativo ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => handleOpenModal("Indicador", indicador)}
                      sx={{ marginRight: 1 }}
                    >
                      Editar
                    </Button>
                    <Button
                      onClick={() =>
                        handleToggleStatus("Indicador", indicador.id)
                      }
                      color={indicador.ativo ? "error" : "success"}
                    >
                      {indicador.ativo ? "Inativar" : "Ativar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Unidades */}
      <Box>
        <Typography
          variant="h5"
          sx={{ color: "primary.main", fontWeight: "bold" }}
        >
          Unidades
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenModal("Unidade")}
          sx={{ marginTop: 2 }}
        >
          Nova Unidade
        </Button>
        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#003366" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Nome
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Tipo de Valor
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unidades.map((unidade) => (
                <TableRow key={unidade.id}>
                  <TableCell>{unidade.nome}</TableCell>
                  <TableCell>{unidade.tipoValor}</TableCell>
                  <TableCell>
                    <Chip
                      label={unidade.ativo ? "Ativo" : "Inativo"}
                      color={unidade.ativo ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => handleOpenModal("Unidade", unidade)}
                      sx={{ marginRight: 1 }}
                    >
                      Editar
                    </Button>
                    <Button
                      onClick={() => handleToggleStatus("Unidade", unidade.id)}
                      color={unidade.ativo ? "error" : "success"}
                    >
                      {unidade.ativo ? "Inativar" : "Ativar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Modal */}
      <Dialog open={!!modalType} onClose={handleCloseModal}>
        <DialogTitle>
          {modalData ? `Editar ${modalType}` : `Novo ${modalType}`}
        </DialogTitle>
        <DialogContent>
          {modalType === "Indicador" && (
            <>
              <TextField
                label="Descrição"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                fullWidth
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Unidade</InputLabel>
                <Select
                  value={unidadeId}
                  onChange={(e) => setUnidadeId(e.target.value)}
                >
                  {unidades
                    .filter((unidade) => unidade.ativo)
                    .map((unidade) => (
                      <MenuItem key={unidade.id} value={unidade.id}>
                        {unidade.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </>
          )}
          {modalType === "Unidade" && (
            <>
              <TextField
                label="Nome"
                value={nomeUnidade}
                onChange={(e) => setNomeUnidade(e.target.value)}
                fullWidth
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Tipo de Valor</InputLabel>
                <Select
                  value={tipoValor}
                  onChange={(e) => setTipoValor(e.target.value)}
                >
                  <MenuItem value="inteiro">Número Inteiro</MenuItem>
                  <MenuItem value="decimal">Número Decimal</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Parametrizacao;
